/**
 * /check-in/session/{id}/job/{jobId} — Per-device page.
 *
 * One vertical page of cards, the same system as the session page:
 *  - the **inspection** (InspectionPanel) — Photos, Identify, Damage, Item
 *    status, Other notes — editable from the moment the device is on the
 *    counter, and still editable (and still complete) after it is finished;
 *  - **Device details** — the pre-lock edit form while those fields can still
 *    change, a read-only card after;
 *  - **Print** — reprints, once the device is past intake.
 *
 * Replaced the four-mode page (edit / awaiting / wizard / done summary) on
 * 2026-10-10. `pickLayout` decides which of those cards are live.
 *
 * Spec: plans/check-in-device-detail.md
 */

import React, { useMemo } from 'react';
import { Alert, Box, CircularProgress, Container } from '@mui/material';
import type { CheckInIntakeType } from '@techgitter/contracts';

import '../styles/checkInMobile.css';
import '../styles/checkInSession.css';
import '../styles/checkInDevice.css';
import { useCheckInSession } from '../hooks/useCheckInSession';
import { DeviceDetailHeader } from '../components/check-in/device/DeviceDetailHeader';
import { DeviceEditForm } from '../components/check-in/device/DeviceEditForm';
import { DeviceOnFile, awaitingMessage } from '../components/check-in/device/DeviceOnFile';
import { InspectionPanel } from '../components/check-in/device/InspectionPanel';
import { CollapsibleSection } from '../components/check-in/sessionDetail/CollapsibleSection';
import { CheckInDevicePrintActions } from '../components/check-in/print/CheckInPrintActions';
import {
  isInStoreIntake,
  outstandingStepNote,
} from '../components/check-in/device/inspectionStage';

export interface CheckInDeviceDetailPageProps {
  sessionId: string;
  jobId: string;
  onBack: () => void;
}

export interface DeviceLayout {
  /** The pre-lock fields: an edit form, or a read-only card. */
  details: 'edit' | 'view';
  inspection: 'editable' | 'readonly' | 'hidden';
}

const EDIT_LIFECYCLES = new Set<string>(['draft_created', 'awaiting_customer_intake']);
/** In-store keeps the pre-lock fields editable through `intake_in_progress`,
 *  which only means the customer opened their link. Mirrors
 *  `_is_structurally_locked` in sm4rt-api — offering the form when the server
 *  would refuse the PATCH (or hiding it when it would accept) is worse than
 *  either rule on its own. */
const IN_STORE_EDIT_LIFECYCLES = new Set<string>([
  'draft_created',
  'awaiting_customer_intake',
  'intake_in_progress',
]);
// Settled, not just paid: a deferral also lets the inspection start.
const PAID_STATES = new Set<string>(['paid', 'paid_overridden', 'deferred', 'deferred_awaiting_payment']);
/** Nothing is accepted against these any more. */
const DEAD_LIFECYCLES = new Set<string>(['cancelled', 'expired']);

export const CheckInDeviceDetailPage: React.FC<CheckInDeviceDetailPageProps> = ({
  sessionId,
  jobId,
  onBack,
}) => {
  const { session, isLoading, error, refresh } = useCheckInSession(sessionId);

  const draftJob = useMemo(() => {
    if (!session) return null;
    return session.draftJobs.find(j => j.id === jobId) ?? null;
  }, [session, jobId]);

  const deviceIndex = useMemo(() => {
    if (!session) return 0;
    return session.draftJobs.findIndex(j => j.id === jobId);
  }, [session, jobId]);

  if (isLoading && !session) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50dvh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !session) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!session || !draftJob) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Device not found on this session.</Alert>
      </Container>
    );
  }

  const lifecycle = session.lifecycleStatus as string;
  const layout = pickLayout(
    lifecycle,
    session.paymentState as string,
    draftJob.inspectionStatus,
    session.intakeType,
  );
  // Only meaningful while inspecting ahead of the workflow; in the ordinary
  // order (payment first) there is nothing outstanding to name.
  const outstanding =
    layout.inspection === 'editable' && draftJob.inspectionStatus !== 'complete'
      ? outstandingStepNote(lifecycle, session.paymentState as string)
      : null;

  return (
    <Box className="checkin-page-root" sx={{ display: 'flex', flexDirection: 'column' }}>
      <DeviceDetailHeader draftJob={draftJob} deviceIndex={deviceIndex} onBack={onBack} />

      <Container maxWidth="md" className="checkin-scroll-area" sx={{ py: 2 }}>
        <div className="ci-device-page">
          {layout.inspection === 'readonly' && (
            <Alert severity="warning">
              This check-in is {lifecycle}. The inspection is shown as it was recorded.
            </Alert>
          )}
          {layout.inspection === 'hidden' && layout.details === 'view' && (
            <Alert severity="info">{awaitingMessage(lifecycle)}</Alert>
          )}
          {outstanding && <Alert severity="info">{outstanding}</Alert>}

          {layout.inspection !== 'hidden' && (
            <InspectionPanel
              // Re-seed the panel's local draft if the page switches device.
              key={draftJob.id}
              sessionId={sessionId}
              draftJob={draftJob}
              readOnly={layout.inspection === 'readonly'}
              onRefresh={refresh}
              onCompleted={() => {
                refresh();
                // Land on the session so staff can move on to the next device
                // or complete the check-in.
                onBack();
              }}
            />
          )}

          {layout.details === 'edit' ? (
            <>
              <DeviceEditForm
                sessionId={sessionId}
                draftJob={draftJob}
                intakeType={session.intakeType ?? null}
                originalWorkItemId={session.originalWorkItemId ?? null}
                // Snapshot taken at session create; matches the same
                // suppression rule the create page applies (see
                // CheckInCreatePage). Edits on a Core / Core+ session
                // re-seed without the diag line.
                isCoreCustomer={
                  session.customerDesignation === 'Core' ||
                  session.customerDesignation === 'Core+'
                }
                onSaved={refresh}
              />
              {!draftJob.serviceSummary && (
                <div className="ci-caption">Service Summary is required.</div>
              )}
            </>
          ) : (
            <CollapsibleSection
              title="Device details"
              defaultOpen={layout.inspection === 'hidden'}
            >
              <DeviceOnFile draftJob={draftJob} />
            </CollapsibleSection>
          )}

          {/* The paperwork for this machine. A completed check-in already sent
              two labels to the counter and the A4 sheet to the office; this is
              the reprint — after a jam, after a serial correction, or for an
              express device whose ticket only exists now. */}
          {layout.details === 'view' && layout.inspection !== 'hidden' && (
            <CollapsibleSection title="Print" defaultOpen={false}>
              <CheckInDevicePrintActions session={session} draftJob={draftJob} />
            </CollapsibleSection>
          )}
        </div>
      </Container>
    </Box>
  );
};

export function pickLayout(
  lifecycle: string,
  paymentState: string,
  inspectionStatus: string,
  intakeType: CheckInIntakeType | string | null | undefined,
): DeviceLayout {
  const inStore = isInStoreIntake(intakeType);
  const details = (inStore ? IN_STORE_EDIT_LIFECYCLES : EDIT_LIFECYCLES).has(lifecycle) ? 'edit' : 'view';

  if (DEAD_LIFECYCLES.has(lifecycle)) {
    // Shown as recorded, never changed — and an unfinished one isn't shown at
    // all, since nobody can finish it.
    return { details: 'view', inspection: inspectionStatus === 'complete' ? 'readonly' : 'hidden' };
  }
  // In-store: the machine is on the counter from the moment the draft exists,
  // so the inspection waits on neither the customer's details nor the fee.
  // Online: it waits until the session is settled or reaches inspection,
  // because nothing is on the bench before then. A completed inspection is
  // always open for correction.
  const inspectable =
    inspectionStatus === 'complete' ||
    inStore ||
    PAID_STATES.has(paymentState) ||
    lifecycle === 'inspection_in_progress';
  return { details, inspection: inspectable ? 'editable' : 'hidden' };
}
